/**
 * L'intorno di un nodo nella mappa della topologia.
 *
 * Le disposizioni stanno in `components/mappaLayout.ts`, comuni a tutte le
 * mappe; qui resta ciò che è solo della topologia: quali nodi mostrare quando
 * si esplora a partire da un apparato.
 */

type LayoutEdge = { source: string; target: string }

/**
 * I nodi raggiungibili da uno di partenza entro un certo numero di salti.
 *
 * È ciò che rende esplorabile una mappa grande: si sceglie un apparato e si
 * guarda **il suo intorno**, invece di cercarlo dentro il groviglio di tutti gli
 * altri. Un salto risponde a «cosa c'è attaccato»; due a «cosa c'è dietro»,
 * che è la domanda successiva.
 */
export function neighbourhood(
  start: string,
  edges: LayoutEdge[],
  depth: number,
): Set<string> {
  const adjacency = new Map<string, string[]>()
  for (const edge of edges) {
    if (!adjacency.has(edge.source)) adjacency.set(edge.source, [])
    if (!adjacency.has(edge.target)) adjacency.set(edge.target, [])
    adjacency.get(edge.source)!.push(edge.target)
    adjacency.get(edge.target)!.push(edge.source)
  }

  const seen = new Set([start])
  let frontier = [start]
  for (let hop = 0; hop < depth; hop++) {
    const next: string[] = []
    for (const id of frontier) {
      for (const peer of adjacency.get(id) ?? []) {
        if (!seen.has(peer)) {
          seen.add(peer)
          next.push(peer)
        }
      }
    }
    if (next.length === 0) break
    frontier = next
  }
  return seen
}
